import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@theme-ui/components';

import FormattedMoney from '@app/components/FormattedMoney';
import useMediaQuery from '@app/helpers/useMediaQuery';
import { screenSmall } from '@app/styles/theme/breakpoints';

import returnMessages from '../ReturnRequest/ReturnRequest.messages';
import type { OrderItemProductFragment } from './__graphql__/OrderHistoryDetailContext';
import messages from './ItemsDetail.messages';
import {
  ItemDescription,
  ItemDescriptionSimplified,
  ItemPricesSimplified,
  ItemSimplified,
  TotalsTableSimplified,
} from './ItemsDetail.styles';
import { alignEnd, DetailTable } from './OrderHistoryDetail.styles';
import { useOrderHistoryDetail } from './OrderHistoryDetailContext';
import TotalsRows from './TotalsRows';

const ItemsDetail = () => {
  const isMobile = useMediaQuery(`(max-width: ${screenSmall})`, {
    // For some reason going mobile first completely breaks table layout
    ssrMatch: false,
  });
  const { itemsData, storeConfig } = useOrderHistoryDetail();
  const includingTax = storeConfig?.taxSalesDisplayPrice === 'including';

  const items = useMemo(
    () =>
      (itemsData?.items ?? []).filter(
        (item) =>
          item && !(typeof item === 'object' && Object.keys(item).length === 0),
      ) as OrderItemProductFragment[],
    [itemsData?.items],
  );

  return isMobile ? (
    <>
      <Box mb={20} data-testid="order-items">
        {items.map((item) =>
          item && item.id ? (
            <ItemSimplified
              pt={20}
              pb={20}
              key={item.id}
              data-testid={`product-${item.productSku}-line`}
            >
              <Box mb={2} data-testid={`product-${item.productSku}-title`}>
                <ItemDescriptionSimplified item={item} />
              </Box>
              <Box mb={4} data-testid={`product-${item.productSku}-status`}>
                {item.status}
              </Box>
              <ItemPricesSimplified>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemPriceHeader} />
                  </dt>
                  <dd data-testid={`product-${item.productSku}-price`}>
                    {includingTax ? (
                      item?.productSalePriceIncludingTax && (
                        <FormattedMoney
                          {...item.productSalePriceIncludingTax}
                        />
                      )
                    ) : (
                      <FormattedMoney {...item.productSalePrice} />
                    )}
                  </dd>
                </Box>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemQuantityHeader} />
                  </dt>
                  <dd data-testid={`product-${item.productSku}-quantity`}>
                    {item.quantityOrdered}
                  </dd>
                </Box>
                <Box mr={20}>
                  <dt style={{ whiteSpace: 'nowrap' }}>
                    <FormattedMessage {...returnMessages.returnRequest} />
                  </dt>
                  <dd data-testid={`product-${item.productSku}-return-status`}>
                    {item.returnStatus ? (
                      <FormattedMessage
                        {...returnMessages.returnRequestSubmitted}
                      />
                    ) : (
                      ''
                    )}
                  </dd>
                </Box>
                <Box mr={20}>
                  <dt>
                    <FormattedMessage {...messages.itemTotalHeader} />
                  </dt>
                  <dd data-testid={`product-${item.productSku}-total-price`}>
                    {includingTax ? (
                      item?.rowTotalIncludingTax && (
                        <FormattedMoney {...item.rowTotalIncludingTax} />
                      )
                    ) : (
                      <FormattedMoney {...item.rowTotal} />
                    )}
                  </dd>
                </Box>
              </ItemPricesSimplified>
            </ItemSimplified>
          ) : null,
        ) ?? null}
      </Box>
      <TotalsTableSimplified>
        <tbody>
          <TotalsRows />
        </tbody>
      </TotalsTableSimplified>
    </>
  ) : (
    <DetailTable>
      <thead>
        <tr>
          <th scope="col">
            <FormattedMessage {...messages.itemProductHeader} />
          </th>
          <th scope="col" css={alignEnd}>
            <FormattedMessage {...messages.itemPriceHeader} />
          </th>
          <th scope="col" css={alignEnd}>
            <FormattedMessage {...messages.itemQuantityHeader} />
          </th>
          <th scope="col" css={alignEnd}>
            <FormattedMessage {...returnMessages.returnRequest} />
          </th>
          <th scope="col" css={alignEnd}>
            <FormattedMessage {...messages.itemTotalHeader} />
          </th>
        </tr>
      </thead>

      <tbody>
        {items.map((item) =>
          item ? (
            <tr
              key={item.productSku}
              data-testid={`product-${item.productSku}-line`}
            >
              <td data-testid={`product-${item.productSku}-title`}>
                <Box mb={3}>
                  <ItemDescription item={item} />
                </Box>
              </td>
              <td
                css={alignEnd}
                data-testid={`product-${item.productSku}-price`}
              >
                {includingTax ? (
                  item?.productSalePriceIncludingTax && (
                    <FormattedMoney {...item.productSalePriceIncludingTax} />
                  )
                ) : (
                  <FormattedMoney {...item.productSalePrice} />
                )}
              </td>
              <td
                css={alignEnd}
                data-testid={`product-${item.productSku}-quantity`}
              >
                {item.quantityOrdered}
              </td>
              <td css={alignEnd}>
                {item.returnStatus ? (
                  <FormattedMessage
                    {...returnMessages.returnRequestSubmitted}
                  />
                ) : (
                  ''
                )}
              </td>
              <td
                css={alignEnd}
                data-testid={`product-${item.productSku}-total-price`}
              >
                {includingTax ? (
                  item?.rowTotalIncludingTax && (
                    <FormattedMoney {...item.rowTotalIncludingTax} />
                  )
                ) : (
                  <FormattedMoney {...item.rowTotal} />
                )}
              </td>
            </tr>
          ) : null,
        ) ?? null}
        <TotalsRows />
      </tbody>
    </DetailTable>
  );
};

export default ItemsDetail;
